import {
  db,
  type Contato,
  type Contrato,
  type ContratoParcela,
  type CronogramaFase,
  type Decisao,
  type LancamentoFinanceiro,
  type MedicaoItem,
  type ParcelaStatus,
} from './db.js'
import {
  addDiarioEntry,
  addLancamento,
  deleteLancamento,
  deleteOrcamentoMaterial,
  escolherOrcamentoMaterial,
  getDiario,
  getLancamentos,
  getObraConfig,
  getOrcamentosByGrupo,
  updateLancamento,
  updateOrcamentoMaterial,
} from './crud.js'
import { salvarFotosObra } from './fotos.js'
import {
  CATEGORIAS_FINANCEIRAS,
  FASES_CONSTRUTIVAS,
  RESERVA_CONTINGENCIA_PADRAO_PCT,
} from '@utils/constants.js'
import { DECISOES_CATALOGO, type DecisaoCatalogo } from '@content/decisoes-catalogo.js'

const now = () => new Date().toISOString()
const today = () => new Date().toISOString().slice(0, 10)

// ── Contatos ─────────────────────────────────────────────────

export type NovoContato = Omit<Contato, 'id' | 'criadoEm' | 'atualizadoEm'>

export async function getContatos(): Promise<Contato[]> {
  const all = await db.contatos.toArray()
  return all.sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
}

export async function getContato(id: number): Promise<Contato | undefined> {
  return db.contatos.get(id)
}

export async function addContato(data: NovoContato): Promise<number> {
  const ts = now()
  return db.contatos.add({ ...data, criadoEm: ts, atualizadoEm: ts })
}

export async function updateContato(id: number, patch: Partial<NovoContato>): Promise<void> {
  await db.contatos.update(id, { ...patch, atualizadoEm: now() })
}

export async function deleteContato(id: number): Promise<void> {
  await db.transaction('rw', [db.contatos, db.contratos], async () => {
    const contratos = await db.contratos.where('contatoId').equals(id).toArray()
    for (const c of contratos) {
      await db.contratos.update(c.id!, { contatoId: undefined, atualizadoEm: now() })
    }
    await db.contatos.delete(id)
  })
}

// ── Contratos ────────────────────────────────────────────────

export type NovoContrato = Omit<Contrato, 'id' | 'criadoEm' | 'atualizadoEm'>
export type NovaParcela = Omit<
  ContratoParcela,
  'id' | 'contratoId' | 'criadoEm' | 'atualizadoEm' | 'status'
> & { status?: ParcelaStatus }

export async function getContratos(): Promise<Contrato[]> {
  return db.contratos.orderBy('criadoEm').reverse().toArray()
}

export async function getContrato(id: number): Promise<Contrato | undefined> {
  return db.contratos.get(id)
}

export async function getParcelas(contratoId?: number): Promise<ContratoParcela[]> {
  const rows = contratoId
    ? await db.contrato_parcelas.where('contratoId').equals(contratoId).toArray()
    : await db.contrato_parcelas.toArray()
  return rows.sort((a, b) => a.ordem - b.ordem)
}

export async function addContrato(data: NovoContrato, parcelas: NovaParcela[]): Promise<number> {
  const ts = now()
  return db.transaction('rw', [db.contratos, db.contrato_parcelas], async () => {
    const id = await db.contratos.add({ ...data, criadoEm: ts, atualizadoEm: ts })
    if (parcelas.length) {
      await db.contrato_parcelas.bulkAdd(
        parcelas.map((p, i) => ({
          ...p,
          ordem: p.ordem ?? i + 1,
          status: p.status ?? 'prevista',
          contratoId: id,
          criadoEm: ts,
          atualizadoEm: ts,
        })),
      )
    }
    return id
  })
}

export async function updateContrato(id: number, patch: Partial<NovoContrato>): Promise<void> {
  await db.contratos.update(id, { ...patch, atualizadoEm: now() })
}

/** Remove contrato e parcelas. Lançamentos já pagos permanecem no financeiro (histórico). */
export async function deleteContrato(id: number): Promise<void> {
  await db.transaction('rw', [db.contratos, db.contrato_parcelas], async () => {
    await db.contrato_parcelas.where('contratoId').equals(id).delete()
    await db.contratos.delete(id)
  })
}

export async function addParcela(contratoId: number, data: NovaParcela): Promise<number> {
  const ts = now()
  const existentes = await getParcelas(contratoId)
  // ordem ausente ou 0 = "no fim da lista"
  const ordem = data.ordem > 0 ? data.ordem : (existentes.at(-1)?.ordem ?? 0) + 1
  return db.contrato_parcelas.add({
    ...data,
    ordem,
    status: data.status ?? 'prevista',
    contratoId,
    criadoEm: ts,
    atualizadoEm: ts,
  })
}

export async function updateParcela(
  id: number,
  patch: Partial<Omit<ContratoParcela, 'id' | 'contratoId' | 'criadoEm'>>,
): Promise<void> {
  await db.contrato_parcelas.update(id, { ...patch, atualizadoEm: now() })
}

export async function deleteParcela(id: number): Promise<void> {
  const parcela = await db.contrato_parcelas.get(id)
  if (parcela?.lancamentoId) await deleteLancamento(parcela.lancamentoId).catch(() => undefined)
  await db.contrato_parcelas.delete(id)
}

export interface MedicaoInput {
  itens: MedicaoItem[]
  observacoes?: string | undefined
  fotos?: (File | Blob)[] | undefined
}

/**
 * Marca serviço medido/aceito: pagamento pode ser feito.
 * Com `medicao`, guarda o checklist na parcela, salva fotos como Documentos e
 * registra uma anotação no diário (prova datada em caso de disputa).
 */
export async function liberarParcela(id: number, medicao?: MedicaoInput): Promise<void> {
  const data = today()
  if (!medicao) {
    await updateParcela(id, { status: 'liberada', liberadaEm: data })
    return
  }

  const parcela = await db.contrato_parcelas.get(id)
  if (!parcela) throw new Error('Parcela não encontrada')
  const contrato = await db.contratos.get(parcela.contratoId)

  const fotoIds =
    medicao.fotos && medicao.fotos.length > 0
      ? await salvarFotosObra(medicao.fotos, {
          observacoes: `Medição · ${contrato?.titulo ?? 'contrato'}: ${parcela.descricao}`,
        })
      : []

  const ok = medicao.itens.filter((i) => i.ok).length
  const total = medicao.itens.length
  const pendentes = medicao.itens.filter((i) => !i.ok).map((i) => i.texto)
  const linhas = [
    `Medição · ${contrato?.titulo ?? 'Contrato'}: ${parcela.descricao} (${formatBRL(parcela.valor)}).`,
    total > 0 ? `${ok}/${total} itens conferidos.` : '',
    pendentes.length > 0 ? `Pendente: ${pendentes.join('; ')}.` : '',
    medicao.observacoes?.trim() ?? '',
  ].filter(Boolean)

  const diarioId = await addDiarioEntry({
    texto: linhas.join(' '),
    contratoId: parcela.contratoId,
    ...(fotoIds.length > 0 ? { fotoIds } : {}),
    ...(contrato?.contatoId != null ? { equipeIds: [contrato.contatoId] } : {}),
  })

  await updateParcela(id, {
    status: 'liberada',
    liberadaEm: data,
    medicao: {
      data,
      itens: medicao.itens,
      ...(medicao.observacoes?.trim() ? { observacoes: medicao.observacoes.trim() } : {}),
      ...(fotoIds.length > 0 ? { fotoIds } : {}),
      diarioId,
    },
  })
}

/** Volta parcela para "prevista". O registro no diário permanece como histórico. */
export async function desfazerLiberacao(id: number): Promise<void> {
  await db.contrato_parcelas.update(id, {
    status: 'prevista',
    liberadaEm: undefined,
    medicao: undefined,
    atualizadoEm: now(),
  })
}

function formatBRL(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
}

export interface PagamentoParcelaInput {
  dataPagamento: string
  formaPagamento: string
  valor?: number
  observacoes?: string
}

/**
 * Registra pagamento da parcela e cria lançamento financeiro correspondente
 * (categoria Mão de Obra, status pago) para manter o saldo da obra consistente.
 */
export async function pagarParcela(id: number, input: PagamentoParcelaInput): Promise<number> {
  const parcela = await db.contrato_parcelas.get(id)
  if (!parcela) throw new Error('Parcela não encontrada')
  const contrato = await db.contratos.get(parcela.contratoId)
  const valor = input.valor ?? parcela.valor

  const lancamento: Omit<LancamentoFinanceiro, 'id' | 'criadoEm' | 'atualizadoEm'> = {
    tipo: 'gasto',
    titulo: `${contrato?.prestador ?? 'Prestador'} · ${parcela.descricao}`,
    valor,
    categoria: 'Mão de Obra',
    status: 'pago',
    formaPagamento: input.formaPagamento,
    data: input.dataPagamento,
    fornecedor: contrato?.prestador ?? '',
    observacoes: [`Contrato: ${contrato?.titulo ?? ''}`, input.observacoes?.trim()]
      .filter(Boolean)
      .join(' · '),
  }
  const lancamentoId = await addLancamento(lancamento)

  // `liberadaEm` fica vazio se pagou sem medir: o resumo trata como adiantamento.
  await updateParcela(id, {
    status: 'paga',
    valor,
    dataPagamento: input.dataPagamento,
    lancamentoId,
    ...(input.observacoes?.trim() ? { observacoes: input.observacoes.trim() } : {}),
  })

  await atualizarStatusContrato(parcela.contratoId)
  return lancamentoId
}

/** Desfaz pagamento: remove lançamento financeiro gerado e volta para "liberada" (ou "prevista" se nunca foi medida). */
export async function desfazerPagamento(id: number): Promise<void> {
  const parcela = await db.contrato_parcelas.get(id)
  if (!parcela) return
  if (parcela.lancamentoId) await deleteLancamento(parcela.lancamentoId).catch(() => undefined)
  await db.contrato_parcelas.update(id, {
    status: parcela.liberadaEm ? 'liberada' : 'prevista',
    dataPagamento: undefined,
    lancamentoId: undefined,
    atualizadoEm: now(),
  })
  await updateContrato(parcela.contratoId, { status: 'ativo' })
}

async function atualizarStatusContrato(contratoId: number): Promise<void> {
  const [contrato, parcelas] = await Promise.all([
    db.contratos.get(contratoId),
    getParcelas(contratoId),
  ])
  if (!contrato || contrato.status === 'cancelado') return
  const todasPagas = parcelas.length > 0 && parcelas.every((p) => p.status === 'paga')
  const novoStatus = todasPagas ? 'concluido' : 'ativo'
  if (contrato.status !== novoStatus) await updateContrato(contratoId, { status: novoStatus })
}

// ── Resumo de contrato ───────────────────────────────────────

export interface ContratoResumo {
  totalParcelas: number
  valorParcelado: number
  pago: number
  liberado: number
  aPagar: number
  pctPago: number
  pctLiberado: number
  /** % do contrato pago sem medição prévia (exclui sinal). Zero = tudo pago por serviço medido. */
  adiantamentoPct: number
  proximaParcela: ContratoParcela | null
  parcelasVencidas: number
}

export function resumirContrato(contrato: Contrato, parcelas: ContratoParcela[]): ContratoResumo {
  const base = contrato.valorTotal > 0 ? contrato.valorTotal : 0
  const valorParcelado = parcelas.reduce((s, p) => s + p.valor, 0)
  const pagas = parcelas.filter((p) => p.status === 'paga')
  const pago = pagas.reduce((s, p) => s + p.valor, 0)
  // Liberado = serviço medido/aceito (ou sinal, que é adiantamento legítimo).
  // Parcela paga sem `liberadaEm` foi paga antes da medição: conta como adiantamento.
  const medida = (p: ContratoParcela) => Boolean(p.liberadaEm) || p.condicao === 'sinal'
  const liberado =
    parcelas.filter((p) => p.status === 'liberada').reduce((s, p) => s + p.valor, 0) +
    pagas.filter(medida).reduce((s, p) => s + p.valor, 0)
  const adiantado = pagas.filter((p) => !medida(p)).reduce((s, p) => s + p.valor, 0)
  const denominador = base > 0 ? base : valorParcelado
  const pct = (v: number) => (denominador > 0 ? Math.round((v / denominador) * 100) : 0)
  const hoje = today()
  const pendentes = parcelas.filter((p) => p.status !== 'paga')
  const proximaParcela =
    [...pendentes].sort((a, b) =>
      (a.dataPrevista ?? '9999').localeCompare(b.dataPrevista ?? '9999'),
    )[0] ?? null
  const parcelasVencidas = pendentes.filter((p) => p.dataPrevista && p.dataPrevista < hoje).length
  return {
    totalParcelas: parcelas.length,
    valorParcelado,
    pago,
    liberado,
    aPagar: Math.max(denominador - pago, 0),
    pctPago: pct(pago),
    pctLiberado: pct(liberado),
    adiantamentoPct: pct(adiantado),
    proximaParcela,
    parcelasVencidas,
  }
}

// ── Gerador de parcelas por marcos ───────────────────────────

export interface GeradorParcelasInput {
  valorTotal: number
  sinalPct: number
  retencaoPct: number
  marcos: string[]
  dataInicio?: string
  dataFim?: string
}

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T12:00:00`)
  d.setDate(d.getDate() + days)
  return d.toISOString().slice(0, 10)
}

function diffDays(a: string, b: string): number {
  const ms = new Date(`${b}T12:00:00`).getTime() - new Date(`${a}T12:00:00`).getTime()
  return Math.round(ms / 86_400_000)
}

/** Distribui o valor em sinal + marcos de medição + retenção final (centavos fechados). */
export function gerarParcelas(input: GeradorParcelasInput): NovaParcela[] {
  const cents = Math.round(input.valorTotal * 100)
  if (cents <= 0) return []
  const sinal = Math.round((cents * input.sinalPct) / 100)
  const retencao = Math.round((cents * input.retencaoPct) / 100)
  const restante = Math.max(cents - sinal - retencao, 0)
  const marcos = input.marcos.filter((m) => m.trim())
  const porMarco = marcos.length ? Math.floor(restante / marcos.length) : 0
  let sobra = marcos.length ? restante - porMarco * marcos.length : restante

  const totalDias =
    input.dataInicio && input.dataFim ? Math.max(diffDays(input.dataInicio, input.dataFim), 0) : 0
  const dataMarco = (i: number, n: number) =>
    input.dataInicio && totalDias > 0
      ? addDays(input.dataInicio, Math.round((totalDias * (i + 1)) / (n + 1)))
      : undefined

  const parcelas: NovaParcela[] = []
  let ordem = 1
  if (sinal > 0) {
    parcelas.push({
      ordem: ordem++,
      descricao: 'Sinal de mobilização',
      valor: sinal / 100,
      condicao: 'sinal',
      ...(input.dataInicio ? { dataPrevista: input.dataInicio } : {}),
    })
  }
  marcos.forEach((m, i) => {
    const extra = sobra > 0 ? 1 : 0
    sobra -= extra
    const valor = (porMarco + extra) / 100
    const data = dataMarco(i, marcos.length)
    parcelas.push({
      ordem: ordem++,
      descricao: m.trim(),
      valor,
      condicao: 'medicao',
      ...(data ? { dataPrevista: data } : {}),
    })
  })
  if (marcos.length === 0 && restante > 0) {
    parcelas.push({
      ordem: ordem++,
      descricao: 'Serviço executado',
      valor: restante / 100,
      condicao: 'medicao',
      ...(input.dataFim ? { dataPrevista: input.dataFim } : {}),
    })
  }
  if (retencao > 0) {
    parcelas.push({
      ordem: ordem++,
      descricao: 'Retenção · vistoria final',
      valor: retencao / 100,
      condicao: 'retencao',
      ...(input.dataFim ? { dataPrevista: input.dataFim } : {}),
    })
  }
  return parcelas
}

// ── Cronograma ───────────────────────────────────────────────

export async function getCronograma(): Promise<CronogramaFase[]> {
  const rows = await db.cronograma_fases.toArray()
  const byId = new Map(rows.map((r) => [r.faseId, r]))
  return FASES_CONSTRUTIVAS.map(
    (f) => byId.get(f.id) ?? { faseId: f.id, avancoPct: 0, atualizadoEm: '' },
  )
}

export async function upsertCronogramaFase(
  faseId: string,
  patch: Partial<Omit<CronogramaFase, 'id' | 'faseId' | 'atualizadoEm'>>,
): Promise<void> {
  const existing = await db.cronograma_fases.where('faseId').equals(faseId).first()
  const base: CronogramaFase = existing ?? { faseId, avancoPct: 0, atualizadoEm: now() }
  const next: CronogramaFase = { ...base, ...patch, faseId, atualizadoEm: now() }

  if (patch.avancoPct !== undefined) {
    const pct = Math.min(Math.max(Math.round(patch.avancoPct), 0), 100)
    next.avancoPct = pct
    if (pct > 0 && !next.inicioReal) next.inicioReal = today()
    if (pct === 100 && !next.fimReal) next.fimReal = today()
    if (pct < 100 && next.fimReal) delete next.fimReal
    if (pct === 0 && next.inicioReal && !existing?.inicioReal) delete next.inicioReal
  }

  await db.cronograma_fases.put(next)
}

export async function saveCronogramaDatas(
  datas: { faseId: string; inicioPrevisto: string; fimPrevisto: string }[],
): Promise<void> {
  for (const d of datas) {
    await upsertCronogramaFase(d.faseId, {
      inicioPrevisto: d.inicioPrevisto,
      fimPrevisto: d.fimPrevisto,
    })
  }
}

/** Pesos típicos de duração por fase (editáveis pelo usuário depois). */
const PESO_FASE: Record<string, number> = {
  'pre-obra': 10,
  infraestrutura: 10,
  superestrutura: 15,
  vedacao: 10,
  cobertura: 8,
  instalacoes: 10,
  'acabamento-bruto': 12,
  'acabamento-fino': 20,
  entrega: 5,
}

/** Sugere datas sequenciais distribuindo a duração total (dias) pelos pesos de fase. */
export function sugerirDatasCronograma(
  dataInicio: string,
  duracaoDias: number,
): { faseId: string; inicioPrevisto: string; fimPrevisto: string }[] {
  const pesoTotal = FASES_CONSTRUTIVAS.reduce((s, f) => s + (PESO_FASE[f.id] ?? 10), 0)
  let cursor = dataInicio
  let acumulado = 0
  return FASES_CONSTRUTIVAS.map((f, i) => {
    acumulado += PESO_FASE[f.id] ?? 10
    const fimOffset =
      i === FASES_CONSTRUTIVAS.length - 1
        ? duracaoDias
        : Math.round((duracaoDias * acumulado) / pesoTotal)
    const inicioPrevisto = cursor
    const fimPrevisto = addDays(dataInicio, fimOffset)
    cursor = addDays(fimPrevisto, 1)
    return { faseId: f.id, inicioPrevisto, fimPrevisto }
  })
}

export interface CronogramaResumo {
  avancoGeral: number
  diasDeObra: number | null
  fasesAtrasadas: string[]
  faseAtualId: string | null
  terminoPrevisto: string | null
  /** Dias de atraso da obra em relação ao fim previsto (0 se em dia). */
  atrasoDias: number
}

export function resumirCronograma(
  fases: CronogramaFase[],
  dataInicioObra?: string,
  dataPrevisaoTermino?: string,
): CronogramaResumo {
  const hoje = today()
  const pesoTotal = fases.reduce((s, f) => s + (PESO_FASE[f.faseId] ?? 10), 0)
  const avancoGeral =
    pesoTotal > 0
      ? Math.round(
          fases.reduce((s, f) => s + f.avancoPct * (PESO_FASE[f.faseId] ?? 10), 0) / pesoTotal,
        )
      : 0
  const fasesAtrasadas = fases
    .filter((f) => f.avancoPct < 100 && f.fimPrevisto && f.fimPrevisto < hoje)
    .map((f) => f.faseId)
  const emAndamento = fases.find((f) => f.avancoPct > 0 && f.avancoPct < 100)
  const primeiraNaoIniciada = fases.find((f) => f.avancoPct === 0)
  const ultimaConcluida = [...fases].reverse().find((f) => f.avancoPct === 100)
  const faseAtualId =
    emAndamento?.faseId ??
    (ultimaConcluida ? (primeiraNaoIniciada?.faseId ?? ultimaConcluida.faseId) : null)
  const ultimoFim = fases
    .map((f) => f.fimPrevisto)
    .filter(Boolean)
    .sort()
    .at(-1)
  const terminoPrevisto = dataPrevisaoTermino ?? ultimoFim ?? null
  const diasDeObra = dataInicioObra ? Math.max(diffDays(dataInicioObra, hoje), 0) : null
  const atrasoDias =
    terminoPrevisto && terminoPrevisto < hoje && avancoGeral < 100
      ? diffDays(terminoPrevisto, hoje)
      : 0
  return { avancoGeral, diasDeObra, fasesAtrasadas, faseAtualId, terminoPrevisto, atrasoDias }
}

/** Sincroniza `faseConstrutiva` da obra com a fase atual do cronograma. */
export async function sincronizarFaseConstrutiva(faseId: string | null): Promise<void> {
  if (!faseId) return
  const config = await getObraConfig()
  if (!config || config.faseConstrutiva === faseId) return
  await db.obra_config.update(1, { faseConstrutiva: faseId, atualizadoEm: now() })
}

// ── Agenda (próximos dias) ───────────────────────────────────

export type AgendaTipo = 'parcela' | 'pendencia' | 'fase' | 'termino'

export interface AgendaItem {
  id: string
  tipo: AgendaTipo
  titulo: string
  detalhe: string
  data: string
  /** Dias até a data (negativo = atrasado). */
  emDias: number
  valor?: number
  rota: string
}

export async function getAgendaProximosDias(
  dias: number,
  rotas: { contrato: (id: number) => string; pendencias: string; cronograma: string },
): Promise<AgendaItem[]> {
  const hoje = today()
  const limite = addDays(hoje, dias)
  const [parcelas, contratos, pendencias, fases, config] = await Promise.all([
    db.contrato_parcelas.toArray(),
    db.contratos.toArray(),
    db.obra_pendencias.toArray(),
    db.cronograma_fases.toArray(),
    getObraConfig(),
  ])
  const contratoById = new Map(contratos.map((c) => [c.id!, c]))
  const faseLabel = new Map(FASES_CONSTRUTIVAS.map((f) => [f.id, f.label]))
  const itens: AgendaItem[] = []

  for (const p of parcelas) {
    if (p.status === 'paga' || !p.dataPrevista || p.dataPrevista > limite) continue
    const c = contratoById.get(p.contratoId)
    if (!c || c.status === 'cancelado') continue
    itens.push({
      id: `parcela-${p.id}`,
      tipo: 'parcela',
      titulo: `${c.prestador} · ${p.descricao}`,
      detalhe: p.status === 'liberada' ? 'Liberada para pagamento' : 'Pagamento previsto',
      data: p.dataPrevista,
      emDias: diffDays(hoje, p.dataPrevista),
      valor: p.valor,
      rota: rotas.contrato(p.contratoId),
    })
  }

  for (const pd of pendencias) {
    if (pd.status === 'resolvida' || !pd.prazo) continue
    const prazo = pd.prazo.slice(0, 10)
    if (prazo > limite) continue
    itens.push({
      id: `pend-${pd.id}`,
      tipo: 'pendencia',
      titulo: pd.titulo,
      detalhe: pd.responsavel ? `Pendência · ${pd.responsavel}` : 'Pendência',
      data: prazo,
      emDias: diffDays(hoje, prazo),
      rota: rotas.pendencias,
    })
  }

  for (const f of fases) {
    if (f.avancoPct >= 100 || !f.fimPrevisto || f.fimPrevisto > limite) continue
    itens.push({
      id: `fase-${f.faseId}`,
      tipo: 'fase',
      titulo: `Fim previsto · ${faseLabel.get(f.faseId) ?? f.faseId}`,
      detalhe: `${f.avancoPct}% concluído`,
      data: f.fimPrevisto,
      emDias: diffDays(hoje, f.fimPrevisto),
      rota: rotas.cronograma,
    })
  }

  if (config?.dataPrevisaoTermino && config.dataPrevisaoTermino <= limite) {
    itens.push({
      id: 'termino',
      tipo: 'termino',
      titulo: 'Previsão de término da obra',
      detalhe: 'Definida nas configurações',
      data: config.dataPrevisaoTermino,
      emDias: diffDays(hoje, config.dataPrevisaoTermino),
      rota: rotas.cronograma,
    })
  }

  return itens.sort((a, b) => a.data.localeCompare(b.data))
}

// ── Semana da obra (o que medir, o que não pagar, prova fotográfica) ─

export type SemanaAcaoTipo = 'medir' | 'pagar'

export interface SemanaAcao {
  parcelaId: number
  contratoId: number
  prestador: string
  tituloContrato: string
  descricao: string
  valor: number
  data: string | null
  emDias: number | null
  acao: SemanaAcaoTipo
}

export interface SemanaDaObra {
  saidaLiberada: number
  valorSemMedir: number
  aMedir: SemanaAcao[]
  aPagar: SemanaAcao[]
  fotosNaSemana: number
  prova: { faseId: string; label: string; dica: string } | null
}

/** O que fotografar antes de fechar a fase atual. */
export const PROVA_FASE: Record<string, string> = {
  'pre-obra': 'Guarde fotos do terreno, vizinhos e do alvará antes de qualquer demolição.',
  infraestrutura: 'Fotografe armação, forma e impermeabilização dos baldrames antes de concretar.',
  superestrutura: 'Registre cada concretagem e as não conformidades no mesmo dia.',
  vedacao:
    'Fotografe prumo, nível e reservas de elétrica e hidráulica antes de fechar a alvenaria.',
  cobertura: 'Fotografe cada camada de impermeabilização antes de telhar.',
  instalacoes: 'Fotografe tubulações, caixas e testes de pressão antes de fechar as paredes.',
  'acabamento-bruto': 'Registre a cura do reboco e as irregularidades antes da massa.',
  'acabamento-fino': 'Fotografe amostras e notas fiscais dos acabamentos escolhidos.',
  entrega: 'Faça o tour fotográfico de entrega com o checklist do Habite-se.',
}

function sortSemanaAcao(a: SemanaAcao, b: SemanaAcao): number {
  if (a.emDias == null && b.emDias == null) return b.valor - a.valor
  if (a.emDias == null) return 1
  if (b.emDias == null) return -1
  return a.emDias - b.emDias || b.valor - a.valor
}

/** O que o dono deve medir, o que não deve pagar e a prova da fase nesta semana. */
export async function getSemanaDaObra(): Promise<SemanaDaObra> {
  const hoje = today()
  const semanaAtras = addDays(hoje, -7)
  const [parcelas, contratos, diario, config, fases] = await Promise.all([
    db.contrato_parcelas.toArray(),
    db.contratos.toArray(),
    getDiario(),
    getObraConfig(),
    getCronograma(),
  ])
  const contratoById = new Map(contratos.map((c) => [c.id!, c]))
  const aMedir: SemanaAcao[] = []
  const aPagar: SemanaAcao[] = []

  for (const p of parcelas) {
    if (p.status === 'paga' || p.id == null) continue
    const c = contratoById.get(p.contratoId)
    if (!c || c.status === 'cancelado' || c.id == null) continue
    const emDias = p.dataPrevista ? diffDays(hoje, p.dataPrevista) : null
    const item: SemanaAcao = {
      parcelaId: p.id,
      contratoId: c.id,
      prestador: c.prestador,
      tituloContrato: c.titulo,
      descricao: p.descricao,
      valor: p.valor,
      data: p.dataPrevista ?? null,
      emDias,
      acao: p.status === 'liberada' || p.condicao === 'sinal' ? 'pagar' : 'medir',
    }
    if (item.acao === 'pagar') aPagar.push(item)
    else aMedir.push(item)
  }

  aMedir.sort(sortSemanaAcao)
  aPagar.sort(sortSemanaAcao)

  const fotosNaSemana = diario.filter(
    (e) => e.criadoEm.slice(0, 10) >= semanaAtras && (e.fotoIds?.length ?? 0) > 0,
  ).length

  const faseId =
    resumirCronograma(fases, config?.dataInicio, config?.dataPrevisaoTermino).faseAtualId ??
    config?.faseConstrutiva ??
    null
  const fase = faseId ? FASES_CONSTRUTIVAS.find((f) => f.id === faseId) : undefined
  const dica = faseId ? (PROVA_FASE[faseId] ?? null) : null
  const prova = fase && dica ? { faseId: fase.id, label: fase.label, dica } : null

  return {
    saidaLiberada: aPagar.reduce((s, i) => s + i.valor, 0),
    valorSemMedir: aMedir.reduce((s, i) => s + i.valor, 0),
    aMedir,
    aPagar,
    fotosNaSemana,
    prova,
  }
}

// ── Memorial de decisões ─────────────────────────────────────

export type NovaDecisao = Omit<Decisao, 'id' | 'criadoEm' | 'atualizadoEm'>

function indiceFase(faseId: string): number {
  const i = FASES_CONSTRUTIVAS.findIndex((f) => f.id === faseId)
  return i < 0 ? 99 : i
}

export async function getDecisoes(): Promise<Decisao[]> {
  const rows = await db.decisoes.toArray()
  return rows.sort((a, b) => b.criadoEm.localeCompare(a.criadoEm))
}

export async function addDecisao(data: NovaDecisao): Promise<number> {
  const ts = now()
  return db.decisoes.add({ ...data, criadoEm: ts, atualizadoEm: ts })
}

export async function updateDecisao(id: number, patch: Partial<NovaDecisao>): Promise<void> {
  await db.decisoes.update(id, { ...patch, atualizadoEm: now() })
}

export async function deleteDecisao(id: number): Promise<void> {
  await db.decisoes.delete(id)
}

/** Catálogo ainda sem registro, a partir da fase em que a escolha deveria existir. */
export function decisoesPendentesDoCatalogo(
  registradas: Decisao[],
  faseAtualId: string | null,
): DecisaoCatalogo[] {
  const feitas = new Set(registradas.map((d) => d.chave).filter((k): k is string => Boolean(k)))
  const idx = faseAtualId ? indiceFase(faseAtualId) : 0
  return DECISOES_CATALOGO.filter((c) => !feitas.has(c.chave) && idx >= indiceFase(c.faseId)).sort(
    (a, b) => indiceFase(a.travaEm) - indiceFase(b.travaEm),
  )
}

/** Catálogo que já trava nesta fase e ainda não foi registrado. */
export function decisoesQueTravemAgora(
  registradas: Decisao[],
  faseAtualId: string | null,
): DecisaoCatalogo[] {
  if (!faseAtualId) return []
  const idx = indiceFase(faseAtualId)
  const feitas = new Set(registradas.map((d) => d.chave).filter((k): k is string => Boolean(k)))
  return DECISOES_CATALOGO.filter((c) => !feitas.has(c.chave) && idx >= indiceFase(c.travaEm))
}

// ── Caixa do mês + ritmo ─────────────────────────────────────

export interface CaixaDoMes {
  mesLabel: string
  anoMes: string
  saidaLiberada: number
  saidaSemMedir: number
  gastoMes: number
  reservaIntacta: number | null
  aposPagar: number | null
  comeReserva: boolean
}

export async function getCaixaDoMes(): Promise<CaixaDoMes> {
  const hoje = today()
  const anoMes = hoje.slice(0, 7)
  const mesLabel = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric' }).format(
    new Date(`${hoje}T12:00:00`),
  )
  const [parcelas, contratos, lancamentos, config] = await Promise.all([
    db.contrato_parcelas.toArray(),
    db.contratos.toArray(),
    getLancamentos(),
    getObraConfig(),
  ])
  const contratoById = new Map(contratos.map((c) => [c.id!, c]))
  let saidaLiberada = 0
  let saidaSemMedir = 0
  for (const p of parcelas) {
    if (p.status === 'paga') continue
    const c = contratoById.get(p.contratoId)
    if (!c || c.status === 'cancelado') continue
    const noMes = p.dataPrevista ? p.dataPrevista.startsWith(anoMes) : true
    if (!noMes) continue
    if (p.status === 'liberada' || p.condicao === 'sinal') saidaLiberada += p.valor
    else saidaSemMedir += p.valor
  }
  const gastoMes = lancamentos
    .filter((l) => l.tipo !== 'receita' && l.status === 'pago' && l.data.startsWith(anoMes))
    .reduce((s, l) => s + l.valor, 0)

  const orcamento = config?.orcamentoTotal ?? 0
  const reservaPct = config?.reservaContingenciaPct ?? RESERVA_CONTINGENCIA_PADRAO_PCT
  const totalGasto = lancamentos
    .filter((l) => l.tipo !== 'receita' && l.status === 'pago')
    .reduce((s, l) => s + l.valor, 0)
  let reservaIntacta: number | null = null
  let aposPagar: number | null = null
  let comeReserva = false
  if (orcamento > 0) {
    const valorReserva = Math.round(orcamento * reservaPct) / 100
    const limite = orcamento - valorReserva
    reservaIntacta = Math.max(0, Math.min(orcamento - totalGasto, valorReserva))
    aposPagar = reservaIntacta - saidaLiberada
    comeReserva = totalGasto + saidaLiberada > limite
  }

  return {
    mesLabel,
    anoMes,
    saidaLiberada,
    saidaSemMedir,
    gastoMes,
    reservaIntacta,
    aposPagar,
    comeReserva,
  }
}

export interface RitmoObra {
  diasSemDiario: number | null
  ultimaData: string | null
  parado: boolean
  chuvasNaSemana: number
}

export async function getRitmoObra(): Promise<RitmoObra> {
  const hoje = today()
  const [diario, config, fases] = await Promise.all([getDiario(), getObraConfig(), getCronograma()])
  const faseId =
    resumirCronograma(fases, config?.dataInicio, config?.dataPrevisaoTermino).faseAtualId ??
    config?.faseConstrutiva ??
    null
  const emPreObra = !faseId || faseId === 'pre-obra'
  const ultima = diario[0]
  const ultimaData = ultima ? ultima.criadoEm.slice(0, 10) : null
  const diasSemDiario = ultimaData ? diffDays(ultimaData, hoje) : diario.length === 0 ? null : 0
  const semanaAtras = addDays(hoje, -7)
  const chuvasNaSemana = diario.filter(
    (e) =>
      e.criadoEm.slice(0, 10) >= semanaAtras && (e.clima === 'chuva' || e.clima === 'chuva-forte'),
  ).length
  const parado = !emPreObra && (diasSemDiario === null || diasSemDiario >= 3)
  return { diasSemDiario, ultimaData, parado, chuvasNaSemana }
}

/** Tem foto de prova no recorte da fase (ou nos últimos 14 dias se sem datas). */
export async function faseTemFotoProva(faseId: string): Promise<boolean> {
  const [fases, diario] = await Promise.all([getCronograma(), getDiario()])
  const fase = fases.find((f) => f.faseId === faseId)
  const fotos = diario.filter((e) => (e.fotoIds?.length ?? 0) > 0)
  const inicio = fase?.inicioReal ?? fase?.inicioPrevisto
  if (!inicio) return fotos.some((e) => e.criadoEm.slice(0, 10) >= addDays(today(), -14))
  return fotos.some((e) => e.criadoEm.slice(0, 10) >= inicio)
}

// ── Materiais → Financeiro ───────────────────────────────────

function categoriaFinanceiraMaterial(categoria?: string): string {
  if (categoria && CATEGORIAS_FINANCEIRAS.includes(categoria)) return categoria
  return 'Materiais'
}

/**
 * Cria ou atualiza o lançamento da cotação.
 * Escolhido = previsto (ainda não saiu da conta). Comprado = pago.
 */
export async function upsertLancamentoMaterial(
  orcamentoId: number,
  statusLanc: 'previsto' | 'pago',
  extra?: { formaPagamento?: string; data?: string },
): Promise<number> {
  const o = await db.orcamentos_materiais.get(orcamentoId)
  if (!o) throw new Error('Cotação não encontrada')

  const payload: Omit<LancamentoFinanceiro, 'id' | 'criadoEm' | 'atualizadoEm'> = {
    tipo: 'gasto',
    titulo: `${o.material} · ${o.fornecedor}`,
    valor: o.valorTotal,
    categoria: categoriaFinanceiraMaterial(o.categoria),
    status: statusLanc,
    formaPagamento: extra?.formaPagamento ?? 'pix',
    data: extra?.data ?? o.data ?? today(),
    fornecedor: o.fornecedor,
    observacoes: `Cotação de material: ${o.material}`,
    ...(o.documentoId != null ? { documentoId: o.documentoId } : {}),
  }

  if (o.lancamentoId) {
    const existing = await db.lancamentos_financeiros.get(o.lancamentoId)
    if (existing) {
      await updateLancamento(o.lancamentoId, payload)
      return o.lancamentoId
    }
  }

  const lancamentoId = await addLancamento(payload)
  await updateOrcamentoMaterial(orcamentoId, { lancamentoId })
  return lancamentoId
}

async function limparLancamentoMaterial(orcamentoId: number): Promise<void> {
  const o = await db.orcamentos_materiais.get(orcamentoId)
  if (!o?.lancamentoId) return
  await deleteLancamento(o.lancamentoId).catch(() => undefined)
  await updateOrcamentoMaterial(orcamentoId, { lancamentoId: undefined })
}

/** Define o vencedor do grupo e lança o valor como previsto no Financeiro. */
export async function escolherOrcamentoELancar(
  grupoId: string,
  escolhidoId: number,
): Promise<void> {
  await escolherOrcamentoMaterial(grupoId, escolhidoId)
  const grupo = await getOrcamentosByGrupo(grupoId)
  for (const o of grupo) {
    if (o.id == null) continue
    if (o.id === escolhidoId) await upsertLancamentoMaterial(o.id, 'previsto')
    else await limparLancamentoMaterial(o.id)
  }
}

/** Marca comprado e promove o lançamento para pago. */
export async function marcarMaterialComprado(
  id: number,
  extra?: { formaPagamento?: string; data?: string },
): Promise<void> {
  await updateOrcamentoMaterial(id, { status: 'comprado' })
  await upsertLancamentoMaterial(id, 'pago', extra)
}

/** Apaga cotação e o lançamento gerado, se existir. */
export async function excluirOrcamentoComLancamento(id: number): Promise<void> {
  await limparLancamentoMaterial(id)
  await deleteOrcamentoMaterial(id)
}
